import { get, MaybeRef } from '@vueuse/shared';
import { isAfter } from 'date-fns';
import isBefore from 'date-fns/isBefore/index';
import parse from 'date-fns/parse';
import { divElementFromHtmlText, htmlToText } from '@front-app/util/content';
import { ValidationRule } from './useValidation';

const EMPTY_STATES = ['', null, undefined, false];

export const required = (additionalEmptyStates: unknown[] = []): ValidationRule<unknown> => {
	const localEmptyStates: unknown[] = [...EMPTY_STATES, ...additionalEmptyStates];
	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		validator(val: unknown): boolean {
			if (Array.isArray(val)) {
				return val.length > 0;
			}
			return !localEmptyStates.includes(val as unknown);
		},
	};
};

/**
 * Allows conditional validation of the primary field, only if a 2nd field is also non-empty.
 *
 * @param other A supplier function that provides the a field to test for emptiness
 * @param additionalEmptyStates
 * @returns Boolean - Returns True if the secondary field is present, and the primary field is
 * also present. Returns True if neither are present. Returns false if the secondary field is
 * present but not the primary field.
 */
export const requiredIfBoth = <T>(other: () => T, additionalEmptyStates: unknown[] = []): ValidationRule<T> => {
	const innerRequired = required(additionalEmptyStates).validator;
	return {
		validator: (val: T) => (innerRequired(other()) ? innerRequired(val) : true),
	};
};

export const atLeastOneIsRequired = <T>(other: () => T, additionalEmptyStates: unknown[] = []): ValidationRule<T> => {
	const innerRequired = required(additionalEmptyStates).validator;
	return {
		validator: (val: T) => (innerRequired(other()) || innerRequired(val)),
	};
};

export const maxLen = (max: number): ValidationRule<string> => ({
	validator(val: string): boolean {
		return !val || (val?.length <= max);
	},
});

export const maxLenHtml = (max: number): ValidationRule<string> => ({
	validator(val: string): boolean {
		return !val || (htmlToText(val).length <= max);
	},
});

export const inRange = (min = Infinity, max = Infinity): ValidationRule<number | string | null> => ({
	validator(val: number | string | null): boolean {
		// Optional values should pass
		if (val === null) {
			return true;
		}
		const innerVal = typeof val === 'number' ? val : parseInt(val, 10);
		return !val || (innerVal >= min && innerVal <= max);
	},
});

export const isFloat = (numDecimals = 2): ValidationRule<string> => {
	const regex = new RegExp(`^\\d*(\\.\\d{0,${numDecimals}})?$`);
	return {
		validator(val: string): boolean {
			return regex.test(val) && !Number.isNaN(parseFloat(val));
		},
	};
};

export const maxParagraphs = (numParas: number): ValidationRule<string> => ({
	validator(val: string): boolean {
		const numParagraphs = divElementFromHtmlText(val).children.length;
		return numParagraphs <= numParas;
	},
});

/**
 * Checks if the input local-datetime is after or equal to a target date
 */
export const isDateOrLater = (startDate: Date): ValidationRule<string> => ({
	validator(val: string): boolean {
		const valAsDate = parse(val, "yyyy-MM-dd'T'kk:mm", new Date());
		return !isBefore(valAsDate, startDate);
	},
});

export const afterDate = (beforeMaybeRef: MaybeRef<Date | string | undefined | null>): ValidationRule<Date | string | undefined | null> => ({
	validator: (val: Date | string | undefined | null) => {
		const before = get(beforeMaybeRef);
		if (!before || !val) {
			return true;
		}
		return isAfter(new Date(val), new Date(before));
	},
});
